import { ref, computed } from "vue";
import { useToastsStore } from "@/stores/toastsStore";

/**
 * Composable for shared template editing logic between slideTemplates and chartTemplates stores
 *
 * Provides unified pattern for:
 * - Tracking pending edits, new templates, and deletions
 * - Batch save operations (delete → create → update)
 * - Reorder with position updates
 * - Reset state
 *
 * @param {Object} options
 * @param {Ref<Array>} options.serverTemplates - Ref to the server templates array
 * @param {Function} options.getIdKey - Function to get the ID key from a template (default: 'id')
 * @returns {Object} Template editing state and actions
 */
export function useTemplateEditing(options = {}) {
  const { serverTemplates, getIdKey = (t) => t.id } = options;
  const toast = useToastsStore();

  // Edit tracking - pending changes before save
  const editedTemplates = ref({}); // { [templateId]: { field: value, ... } }
  const pendingNewTemplates = ref([]); // Templates to create on save
  const pendingDeletions = ref(new Set()); // Template IDs to delete on save
  const saving = ref(false);

  // Counter for temporary IDs (negative to distinguish from real IDs)
  let tempIdCounter = -1;

  /**
   * Get the next temporary ID for new templates
   */
  function getNextTempId() {
    return tempIdCounter--;
  }

  /**
   * Check if there are unsaved changes
   */
  const hasChanges = computed(() => {
    return (
      Object.keys(editedTemplates.value).length > 0 ||
      pendingNewTemplates.value.length > 0 ||
      pendingDeletions.value.size > 0
    );
  });

  /**
   * Get template data with pending edits applied
   * @param {Array} templates - The full templates list (server + pending)
   * @param {number|string} templateId - Template ID to look up
   */
  function getEffectiveTemplateData(templates, templateId) {
    const template = templates.find((t) => getIdKey(t) === templateId);
    if (!template) return null;
    const edits = editedTemplates.value[templateId];
    if (!edits) return template;
    return { ...template, ...edits };
  }

  /**
   * Update template locally (without saving to API)
   * @param {number|string} templateId - Template ID
   * @param {string} field - Field name to update
   * @param {*} value - New value
   */
  function updateLocalTemplate(templateId, field, value) {
    // Check if it's a pending new template
    const pendingIndex = pendingNewTemplates.value.findIndex((t) => getIdKey(t) === templateId);
    if (pendingIndex !== -1) {
      pendingNewTemplates.value[pendingIndex][field] = value;
      return;
    }

    // Otherwise it's an existing template
    if (!editedTemplates.value[templateId]) {
      editedTemplates.value[templateId] = {};
    }
    editedTemplates.value[templateId][field] = value;
  }

  /**
   * Add a new template locally (will be created on save)
   * @param {Object} templateData - Template data to add
   * @returns {Object} The added template with temp ID
   */
  function addPendingTemplate(templateData) {
    const tempId = getNextTempId();
    const newTemplate = {
      ...templateData,
      id: tempId,
      _isNew: true,
    };
    pendingNewTemplates.value.push(newTemplate);
    return newTemplate;
  }

  /**
   * Mark a template for deletion (will be deleted on save)
   * @param {number|string} templateId - Template ID to remove
   * @param {Ref} currentSelectionRef - Optional ref to current selection ID
   * @param {Function} getTemplates - Function to get current templates list
   */
  function removeTemplate(templateId, currentSelectionRef = null, getTemplates = null) {
    // If it's a pending new template, just remove it from the list
    const pendingIndex = pendingNewTemplates.value.findIndex((t) => getIdKey(t) === templateId);
    if (pendingIndex !== -1) {
      pendingNewTemplates.value.splice(pendingIndex, 1);
    } else {
      // Mark existing template for deletion
      pendingDeletions.value.add(templateId);
    }

    // Select another template if the deleted one was selected
    if (currentSelectionRef && currentSelectionRef.value === templateId && getTemplates) {
      const remaining = getTemplates().filter((t) => getIdKey(t) !== templateId);
      currentSelectionRef.value = remaining.length > 0 ? getIdKey(remaining[0]) : null;
    }
  }

  /**
   * Reorder templates (after drag-drop) - updates positions for save
   * @param {Array} orderedIds - Ordered list of template IDs
   */
  function reorderTemplates(orderedIds) {
    // Separate server templates from pending templates
    const serverIds = orderedIds.filter((id) => id > 0);
    const pendingIds = orderedIds.filter((id) => id < 0);

    // Reorder serverTemplates array to match the new order
    const reorderedServer = serverIds
      .map((id) => serverTemplates.value.find((t) => getIdKey(t) === id))
      .filter(Boolean);
    serverTemplates.value = reorderedServer;

    // Update positions for save (all server templates get new positions)
    serverIds.forEach((id, index) => {
      const newPosition = index + 1;
      if (!editedTemplates.value[id]) {
        editedTemplates.value[id] = {};
      }
      editedTemplates.value[id].position = newPosition;
    });

    // Update pending templates positions
    pendingIds.forEach((id, index) => {
      const pendingTemplate = pendingNewTemplates.value.find((t) => getIdKey(t) === id);
      if (pendingTemplate) {
        pendingTemplate.position = serverIds.length + index + 1;
        delete pendingTemplate._insertAfter;
      }
    });
  }

  /**
   * Save all pending changes
   * @param {Object} callbacks - Server operation callbacks
   * @param {Function} callbacks.createOnServer - (templateData) => Promise<createdTemplate>
   * @param {Function} callbacks.updateOnServer - (id, changes) => Promise<updatedTemplate>
   * @param {Function} callbacks.deleteOnServer - (id) => Promise<boolean>
   * @param {Function} callbacks.refetchTemplates - () => Promise<void>
   * @param {Ref} [currentSelectionRef] - Optional ref to current selection ID (for temp ID → real ID mapping)
   * @param {Function} [onTemplateCreated] - Optional callback when a template is created (receives { tempId, created })
   * @returns {Promise<boolean>} True if save succeeded
   */
  async function saveAllChanges(callbacks, currentSelectionRef = null, onTemplateCreated = null) {
    if (!hasChanges.value) return true;

    const { createOnServer, updateOnServer, deleteOnServer, refetchTemplates } = callbacks;
    saving.value = true;

    try {
      // Track if current selection was a temp ID
      const currentWasTempId = currentSelectionRef && currentSelectionRef.value < 0;
      let newIdForSelection = null;

      // 1. Delete templates marked for deletion (parallel)
      // Filter out templates that no longer exist on the server (already deleted by sync)
      const deletionIds = [...pendingDeletions.value].filter((id) =>
        serverTemplates.value.some((t) => getIdKey(t) === id),
      );
      await Promise.all(deletionIds.map((templateId) => deleteOnServer(templateId)));
      pendingDeletions.value = new Set();

      // 2. Create new templates (parallel)
      const pendingToCreate = [...pendingNewTemplates.value];
      if (pendingToCreate.length > 0) {
        const creationResults = await Promise.all(
          pendingToCreate.map(async (pendingTemplate) => {
            const created = await createOnServer(pendingTemplate);
            if (onTemplateCreated) {
              onTemplateCreated({ tempId: getIdKey(pendingTemplate), created });
            }
            return { tempId: getIdKey(pendingTemplate), created };
          }),
        );

        // Track new ID for selection if current was a temp ID
        if (currentWasTempId && currentSelectionRef) {
          const createdMatch = creationResults.find((c) => c.tempId === currentSelectionRef.value);
          if (createdMatch) {
            newIdForSelection = getIdKey(createdMatch.created);
          }
        }
      }
      pendingNewTemplates.value = [];

      // 3. Update edited templates (parallel)
      const editsToSave = Object.entries(editedTemplates.value);
      if (editsToSave.length > 0) {
        await Promise.all(
          editsToSave.map(async ([templateId, changes]) => {
            await updateOnServer(parseInt(templateId), changes);
          }),
        );
      }
      editedTemplates.value = {};

      // 4. Re-fetch all templates to get correct state from server
      await refetchTemplates();

      // Update selection to the new real ID
      if (newIdForSelection && currentSelectionRef) {
        currentSelectionRef.value = newIdForSelection;
      }

      return true;
    } catch (e) {
      toast.error("Failed to save changes");
      throw e;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Clear all edit state
   */
  function clearEditState() {
    editedTemplates.value = {};
    pendingNewTemplates.value = [];
    pendingDeletions.value = new Set();
  }

  /**
   * Full reset including temp ID counter
   */
  function $reset() {
    clearEditState();
    saving.value = false;
    tempIdCounter = -1;
  }

  return {
    // State
    editedTemplates,
    pendingNewTemplates,
    pendingDeletions,
    saving,
    hasChanges,

    // Actions
    getNextTempId,
    getEffectiveTemplateData,
    updateLocalTemplate,
    addPendingTemplate,
    removeTemplate,
    reorderTemplates,
    saveAllChanges,
    clearEditState,
    $reset,
  };
}

export default useTemplateEditing;
